import { Schema as S, Struct } from "effect";
import { EgkAttestationHandle } from "../patient-info/egk-attestation.schema";
import { PatientDemographicData } from "./demographics.schema";
import { MatchedPatient } from "./matched-patient.schema";
import { FieldDiff } from "./field-diff.schema";

/**
 * Actionable and completed states exposed by the card workflow.
 * @since 1.1.11
 * @example
 * const status = CardReadWorkflowStatus.make("MERGED");
 */
export const CardReadWorkflowStatus = S.Literals([
  "APPLIED",
  "MATCHING",
  "MERGED",
  "CANCELLED",
]);
/**
 * Decoded card-workflow state.
 * @since 1.1.11
 * @example
 * const status: CardReadWorkflowStatus = "MERGED";
 */
export type CardReadWorkflowStatus = S.Schema.Type<
  typeof CardReadWorkflowStatus
>;

/**
 * Actions supported by the inSuite card workflow.
 * @since 1.1.11
 * @example
 * const action = CardReadAction.make("apply");
 */
export const CardReadAction = S.Literals(["apply", "cancel", "matching_select"]);
/**
 * Decoded card-workflow action.
 * @since 1.1.11
 * @example
 * const action: CardReadAction = "apply";
 */
export type CardReadAction = S.Schema.Type<typeof CardReadAction>;

/**
 * Usable card result normalized by the Bridge for all FrontDesk+ clients.
 * @since 1.1.11
 * @example
 * const result = CardReadWorkflowResult.make({ status: "APPLIED", crlogId: "read-1", possibleActions: [], cardData: { givenName: "", familyName: "", birthDate: "", gender: "unknown", insurance: { insuranceId: "", insuranceName: "", insuranceType: "statutory", validFrom: "" } } });
 */
export const CardReadWorkflowResult = S.Struct({
  status: CardReadWorkflowStatus,
  crlogId: S.String,
  patientId: S.optional(S.String), // Patient ID when APPLIED/MERGED
  possibleActions: S.Array(CardReadAction),
  validationStatus: S.optional(S.String),
  cardData: PatientDemographicData,
  matchedPatients: S.optional(S.Array(MatchedPatient)),
  diff: S.optional(S.Array(FieldDiff)),
  /**
   * Opaque proof that this flow was established by a server-performed eGK read.
   *
   * Present only while the patient-info policy is enabled and its store is available. The client
   * carries it in volatile flow memory across appointment selection / card-data application and
   * supplies it to check-in or card registration; it is not authority to read patient data and
   * confers nothing until a real commit. Type is owned by `@ascleo/frontdesk-contracts`.
   */
  egkAttestation: S.optional(EgkAttestationHandle),
});
/**
 * Decoded normalized card result.
 * @since 1.1.11
 * @example
 * const result: CardReadWorkflowResult = { status: "APPLIED", crlogId: "read-1", possibleActions: [], cardData: { givenName: "", familyName: "", birthDate: "", gender: "unknown", insurance: { insuranceId: "", insuranceName: "", insuranceType: "statutory", validFrom: "" } } };
 */
export type CardReadWorkflowResult = S.Schema.Type<
  typeof CardReadWorkflowResult
>;

/**
 * An action acknowledgement does not require a second copy of card demographics.
 * @since 1.1.10
 * @example
 * CardActionWorkflowResult.make({ status: "APPLIED", crlogId: "read-1", possibleActions: [] });
 */
export const CardActionWorkflowResult = CardReadWorkflowResult.mapFields(Struct.omit(["cardData"]));

/**
 * Decoded acknowledgement for apply, select and cancel operations.
 * @since 1.1.10
 * @example
 * const result: CardActionWorkflowResult = { status: "APPLIED", crlogId: "read-1", possibleActions: [] };
 */
export type CardActionWorkflowResult = typeof CardActionWorkflowResult.Type;
